import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ApiError, type ApiErrorBody, errorMessage, gebruiktMockApi, http } from './api'
import {
  mockBevestigVoorbeeld,
  mockOntvinkMelding,
  mockUploads,
  mockVerwerpVoorbeeld,
  mockVinkMelding,
  mockVoorbeeld,
} from './__fixtures__/beheer-fixtures'
import { vertraag } from './__fixtures__/vertraag'
import type { Afvinkantwoord, Afvinking, Bevestiging, Upload, Voorbeeld } from './rooster-types'

export interface RoosterApi {
  uploads(): Promise<Upload[]>
  voorbeeld(bestand: File): Promise<Voorbeeld>
  bevestig(bevestiging: Bevestiging): Promise<Voorbeeld>
  verwerp(voorbeeldId: string): Promise<void>
  vink(sleutel: string, bericht: string): Promise<Afvinkantwoord>
  ontvink(sleutel: string): Promise<void>
}

/**
 * `http.del` stuurt geen body mee, terwijl het ontvinken de sleutel in de body
 * verwacht. Deze aanroep doet dat wel en houdt de foutvorm van `http` aan.
 */
async function verwijderMetBody(pad: string, body: unknown): Promise<void> {
  const antwoord = await fetch(pad, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  if (antwoord.ok) return

  const terugval: ApiErrorBody = {
    error: { code: `http_${antwoord.status}`, message: antwoord.statusText || 'Onbekende fout' },
  }
  const isJson = (antwoord.headers.get('content-type') ?? '').includes('json')
  const uitgepakt = isJson ? await antwoord.json().catch(() => terugval) : terugval

  throw new ApiError(antwoord.status, uitgepakt as ApiErrorBody)
}

class RealRoosterApi implements RoosterApi {
  uploads(): Promise<Upload[]> {
    return http.get<Upload[]>('/api/rooster/uploads')
  }
  voorbeeld(bestand: File): Promise<Voorbeeld> {
    return http.upload<Voorbeeld>('/api/rooster/voorbeeld', 'bestand', bestand)
  }
  bevestig(bevestiging: Bevestiging): Promise<Voorbeeld> {
    return http.post<Voorbeeld>(
      `/api/rooster/voorbeeld/${encodeURIComponent(bevestiging.voorbeeldId)}/bevestig`,
      { bestandsnaam: bevestiging.bestandsnaam },
    )
  }
  verwerp(voorbeeldId: string): Promise<void> {
    return http.del<void>(`/api/rooster/voorbeeld/${encodeURIComponent(voorbeeldId)}`)
  }
  vink(sleutel: string, bericht: string): Promise<Afvinkantwoord> {
    return http.put<Afvinkantwoord>('/api/meldingen/afvinken', { sleutel, bericht })
  }
  ontvink(sleutel: string): Promise<void> {
    return verwijderMetBody('/api/meldingen/afvinken', { sleutel })
  }
}

class MockRoosterApi implements RoosterApi {
  uploads(): Promise<Upload[]> {
    return vertraag(mockUploads())
  }
  voorbeeld(bestand: File): Promise<Voorbeeld> {
    return vertraag(mockVoorbeeld(bestand), 700)
  }
  bevestig(bevestiging: Bevestiging): Promise<Voorbeeld> {
    return vertraag(mockBevestigVoorbeeld(bevestiging.voorbeeldId, bevestiging.bestandsnaam), 700)
  }
  verwerp(voorbeeldId: string): Promise<void> {
    return vertraag(mockVerwerpVoorbeeld(voorbeeldId))
  }
  vink(sleutel: string, bericht: string): Promise<Afvinkantwoord> {
    return vertraag(mockVinkMelding(sleutel, bericht), 120)
  }
  ontvink(sleutel: string): Promise<void> {
    return vertraag(mockOntvinkMelding(sleutel), 120)
  }
}

export const roosterApi: RoosterApi = gebruiktMockApi ? new MockRoosterApi() : new RealRoosterApi()

export const roosterKeys = {
  alle: ['rooster'] as const,
  uploads: () => ['rooster', 'uploads'] as const,
}

export function useUploads() {
  return useQuery({ queryKey: roosterKeys.uploads(), queryFn: () => roosterApi.uploads() })
}

export function useMaakVoorbeeld() {
  return useMutation({
    mutationFn: (bestand: File) => roosterApi.voorbeeld(bestand),
    onError: (error) => toast.error(errorMessage(error, 'Het bestand kon niet gelezen worden')),
  })
}

export function useBevestigVoorbeeld() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (bevestiging: Bevestiging) => roosterApi.bevestig(bevestiging),
    onSuccess: (resultaat) => {
      queryClient.invalidateQueries()
      toast.success(`${resultaat.bestandsnaam} is geïmporteerd`)
    },
    onError: (error) => toast.error(errorMessage(error, 'Importeren mislukt')),
  })
}

export function useVerwerpVoorbeeld() {
  return useMutation({
    mutationFn: (voorbeeldId: string) => roosterApi.verwerp(voorbeeldId),
    onError: (error) => toast.error(errorMessage(error, 'Het voorbeeld opruimen mislukte')),
  })
}

export function useVinkMelding() {
  return useMutation({
    mutationFn: async (afvinking: Afvinking): Promise<Afvinkantwoord | null> => {
      if (!afvinking.afgevinkt) {
        await roosterApi.ontvink(afvinking.sleutel)
        return null
      }
      return roosterApi.vink(afvinking.sleutel, afvinking.bericht)
    },
    onError: (error) => toast.error(errorMessage(error, 'Afvinken mislukt')),
  })
}
